import {
  QuestionnaireResponses,
  WorkflowEventState,
  WorkflowEventTypes,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import { MplServiceClientFactory } from './MplServiceClientFactory';
import { promiseTimeout } from './UtilsApi';
import FlashbarMessageBroker from '../common/FlashbarMessageBroker';

const DEFAULT_MAX_POLL_ATTEMPTS = 7;
const DEFAULT_POLL_INTERVAL_MS = 2000;

export interface CreateWorkflowEventWithStatePollingOptions {
  readonly prevWorkflowId?: number;
  readonly maxPollAttempts?: number;
  readonly pollIntervalMs?: number;
  readonly suppressSuccessFlashbar?: boolean;
}

export const createWorkflowEventWithStatePolling = async (
  contentId: string,
  workflowId: number,
  eventType: WorkflowEventTypes,
  comment: string,
  data?: object,
  evaluation?: QuestionnaireResponses,
  score?: number,
  options: CreateWorkflowEventWithStatePollingOptions = {},
) => {
  const mplServiceClient = MplServiceClientFactory.create();
  const flashbarMessageBroker = FlashbarMessageBroker.create();
  const {
    prevWorkflowId,
    maxPollAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    suppressSuccessFlashbar,
  } = options;
  const pollStatus = async (eventId: string) => {
    let attempts = 0;
    while (attempts < maxPollAttempts) {
      await promiseTimeout(pollIntervalMs);
      try {
        const { event } = await mplServiceClient.getWorkflowEvent(eventId);
        if (event.state === WorkflowEventState.APPLIED) {
          return;
        }
        if (event.state === WorkflowEventState.DISCARDED) {
          throw new Error('Content action could not be processed successfully.');
        }
      } catch (error) {
        // @ts-expect-error TS18046 - strict migration
        if (error.response.status === 404) {
          // This could happen if event is not found as it is just created. In this case retry and see if it is created.
        } else {
          throw error;
        }
      }
      attempts++;
    }

    throw new Error('Content action processing timed out');
  };

  if (eventType === WorkflowEventTypes.LOCK) {
    const { workflow } = await mplServiceClient.getContentWorkflow(contentId);
    if (workflow.publicationState === WorkflowState.FINALIZING) {
      flashbarMessageBroker.pushErrorMessage(
        'Content is currently finalizing by TW. Unable to lock',
      );
      return;
    }
  }
  const postWorkflowEventArgs = [
    contentId,
    prevWorkflowId ?? workflowId,
    eventType,
    comment,
    data,
    evaluation,
    score,
  ] as const;
  const { eventId: wfEventId } = suppressSuccessFlashbar
    ? await mplServiceClient.postWorkflowEvent(...postWorkflowEventArgs, {
        suppressSuccessFlashbar,
      })
    : await mplServiceClient.postWorkflowEvent(...postWorkflowEventArgs);
  await pollStatus(wfEventId);
};
